/* Product detail enhancement: media switching, future-safe variant state,
   quantity steppers, and a contextual mobile purchase action. The native
   Shopify product form and all media remain usable without JavaScript. */
(() => {
  const CART_UPDATED = 'pf:cart:updated';

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.links = [...this.querySelectorAll('[data-media-target]')];
      this.panels = [...this.querySelectorAll('[data-media-panel]')];
      this.status = this.querySelector('[data-gallery-status]');
      if (!this.panels.length) return;

      this.classList.add('is-enhanced');
      this.links.forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectMedia(link.dataset.mediaTarget, true);
        });
      });

      const initial = this.querySelector('[data-media-panel].is-active') || this.panels[0];
      this.selectMedia(initial.dataset.mediaPanel, false);
    }

    selectMedia(mediaId, announce = false) {
      const target = this.panels.find((panel) => panel.dataset.mediaPanel === String(mediaId));
      if (!target) return;

      this.panels.forEach((panel) => {
        const active = panel === target;
        panel.classList.toggle('is-active', active);
        panel.toggleAttribute('hidden', !active);
        if (!active) {
          panel.querySelectorAll('video').forEach((video) => video.pause());
        }
      });

      this.links.forEach((link) => {
        const active = link.dataset.mediaTarget === String(mediaId);
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });

      if (announce && this.status) {
        const index = this.panels.indexOf(target) + 1;
        this.status.textContent = `Showing media ${index} of ${this.panels.length}.`;
      }
    }
  }

  class ProductExperience extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-product-form]');
      this.quantity = this.querySelector('[data-product-quantity]');
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.primarySubmit = this.querySelector('[data-primary-submit]');
      this.sticky = this.querySelector('[data-sticky-atc]');
      this.footer = document.querySelector('footer');
      this.actionVisible = true;
      this.footerVisible = false;

      this.classList.add('is-enhanced');
      this.querySelectorAll('[data-quantity-step]').forEach((button) => button.removeAttribute('hidden'));
      this.addEventListener('click', (event) => this.onClick(event));
      this.variantSelect?.addEventListener('change', () => this.updateVariant());
      this.form?.addEventListener('submit', () => this.setBusy(true));

      this.onCartUpdated = () => {
        this.setBusy(false);
        const status = this.querySelector('[data-product-status]');
        if (status) status.textContent = this.dataset.addedStatus || 'Added to cart.';
      };
      document.addEventListener(CART_UPDATED, this.onCartUpdated);

      this.setupStickyAction();
      this.updateVariant(false);
    }

    disconnectedCallback() {
      document.removeEventListener(CART_UPDATED, this.onCartUpdated);
      this.actionObserver?.disconnect();
      this.footerObserver?.disconnect();
    }

    onClick(event) {
      const step = event.target.closest('[data-quantity-step]');
      if (!step || !this.quantity) return;
      event.preventDefault();
      if (step.dataset.quantityStep === 'increase') this.quantity.stepUp();
      else this.quantity.stepDown();
      this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateVariant(updateUrl = true) {
      const option = this.variantSelect?.selectedOptions?.[0];
      if (!option) return;

      const available = option.dataset.available === 'true';
      const price = option.dataset.priceLabel || '';
      const mediaId = option.dataset.mediaId;
      const soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      const addLabel = this.dataset.addLabel || 'Add to cart';

      this.querySelectorAll('[data-variant-price]').forEach((element) => {
        element.textContent = price;
      });
      this.querySelectorAll('[data-add-price]').forEach((element) => {
        element.textContent = price;
      });
      this.querySelectorAll('[data-add-label]').forEach((element) => {
        element.textContent = available ? addLabel : soldOutLabel;
      });
      this.querySelectorAll('[data-product-submit]').forEach((button) => {
        button.disabled = !available;
        button.setAttribute('aria-disabled', available ? 'false' : 'true');
      });

      const availability = this.querySelector('[data-product-availability]');
      if (availability) {
        availability.textContent = available
          ? (this.dataset.availableLabel || 'Available')
          : soldOutLabel;
      }

      const barcode = this.querySelector('[data-variant-barcode]');
      if (barcode) {
        barcode.textContent = option.dataset.barcode || '';
        barcode.closest('[data-detail-row]')?.toggleAttribute('hidden', !option.dataset.barcode);
      }

      if (mediaId) this.querySelector('product-gallery')?.selectMedia(mediaId, false);
      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      }
    }

    setBusy(busy) {
      const label = busy ? (this.dataset.addingLabel || 'Adding') : (this.dataset.addLabel || 'Add to cart');
      this.querySelectorAll('[data-product-submit]').forEach((button) => {
        if (button.getAttribute('aria-disabled') === 'true' && !busy) return;
        button.toggleAttribute('aria-busy', busy);
      });
      if (busy) {
        this.querySelectorAll('[data-add-label]').forEach((element) => {
          element.textContent = label;
        });
      } else {
        this.updateVariant(false);
      }
    }

    setupStickyAction() {
      if (!this.sticky || !this.primarySubmit || !('IntersectionObserver' in window)) return;

      this.actionObserver = new IntersectionObserver(([entry]) => {
        this.actionVisible = entry.isIntersecting;
        this.syncStickyAction();
      }, { threshold: 0.2 });
      this.actionObserver.observe(this.primarySubmit);

      if (this.footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.footerVisible = entry.isIntersecting;
          this.syncStickyAction();
        }, { threshold: 0.01 });
        this.footerObserver.observe(this.footer);
      }
    }

    syncStickyAction() {
      const visible = !this.actionVisible && !this.footerVisible;
      this.sticky.classList.toggle('is-visible', visible);
      this.sticky.toggleAttribute('aria-hidden', !visible);
    }
  }

  if (!window.customElements.get('product-gallery')) {
    window.customElements.define('product-gallery', ProductGallery);
  }
  if (!window.customElements.get('product-experience')) {
    window.customElements.define('product-experience', ProductExperience);
  }
})();
